import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Collapse, HStack, IconButton, Input, SimpleGrid, Wrap, WrapItem } from '@chakra-ui/react';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useEffect, useMemo, useState } from 'react';

import Link from 'components/Link';
import Text from 'components/Text';

const POPULAR_FONTS = [
  { name: 'Inter', weights: '400;500;600;700', category: 'sans-serif' },
  { name: 'Roboto', weights: '400;500;700', category: 'sans-serif' },
  { name: 'Open Sans', weights: '400;600;700', category: 'sans-serif' },
  { name: 'Lato', weights: '400;700', category: 'sans-serif' },
  { name: 'Montserrat', weights: '400;500;600;700', category: 'sans-serif' },
  { name: 'Poppins', weights: '400;500;600;700', category: 'sans-serif' },
  { name: 'Playfair Display', weights: '400;700', category: 'serif' },
  { name: 'Merriweather', weights: '400;700', category: 'serif' },
  { name: 'Lora', weights: '400;700', category: 'serif' },
  { name: 'Source Code Pro', weights: '400;500;700', category: 'monospace' },
  { name: 'Fira Code', weights: '400;500;700', category: 'monospace' },
  { name: 'JetBrains Mono', weights: '400;500;700', category: 'monospace' },
  { name: 'Oswald', weights: '400;500;700', category: 'display' },
  { name: 'Raleway', weights: '400;500;600;700', category: 'sans-serif' },
  { name: 'Nunito', weights: '400;600;700', category: 'sans-serif' },
  { name: 'Work Sans', weights: '400;500;600;700', category: 'sans-serif' },
];

const FONT_CATEGORIES = ['all', 'sans-serif', 'serif', 'monospace', 'display'];

const generateFontUrl = (fontName: string, weights: string) => {
  const encodedName = fontName.replace(/ /g, '+');
  return `https://fonts.googleapis.com/css2?family=${encodedName}:wght@${weights}&display=swap`;
};

const generateLinkTag = (fontName: string, weights: string) => {
  const url = generateFontUrl(fontName, weights);
  return `<link href="${url}" rel="stylesheet">`;
};

const generateCSSImport = (fontName: string, weights: string) => {
  const url = generateFontUrl(fontName, weights);
  return `@import url('${url}');`;
};

const FontsTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [copyMode, setCopyMode] = useState<'link' | 'import'>('link');
  const [expandedFont, setExpandedFont] = useState<string | null>(null);
  const { toastSuccess } = useToastWrapper();

  const filteredFonts = useMemo(() => {
    return POPULAR_FONTS.filter((font) => {
      const matchesSearch = font.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || font.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  // Load Google Fonts on demand for preview
  useEffect(() => {
    filteredFonts.forEach((font) => {
      const fontId = `font-preview-${font.name.replace(/\s/g, '-')}`;
      if (document.getElementById(fontId)) return;

      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href = generateFontUrl(font.name, font.weights);
      document.head.appendChild(link);
    });
  }, [filteredFonts]);

  const handleCopyFont = (font: (typeof POPULAR_FONTS)[0]) => {
    const code =
      copyMode === 'link' ? generateLinkTag(font.name, font.weights) : generateCSSImport(font.name, font.weights);
    navigator.clipboard.writeText(code);
    toastSuccess(`Copied ${font.name} ${copyMode === 'link' ? 'link tag' : 'CSS import'}!`);
  };

  const handleCopyFontFamily = (fontName: string, category: string) => {
    const css = `font-family: '${fontName}', ${category};`;
    navigator.clipboard.writeText(css);
    toastSuccess(`Copied: ${css}`);
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Text fontSize="large" fontWeight="semibold" mb={2}>
          Google Fonts
        </Text>
        <Text fontSize="small" opacity="secondary">
          Click the specimen to copy font-family CSS. Use the copy button for import code.
        </Text>
      </Box>

      {/* Copy mode toggle */}
      <Wrap spacing={2} mb={3}>
        <WrapItem>
          <Text fontSize="small" opacity="secondary" lineHeight="28px">
            Copy as:
          </Text>
        </WrapItem>
        <WrapItem>
          <Box
            px={3}
            py={1}
            borderRadius="full"
            fontSize="small"
            cursor="pointer"
            bg={copyMode === 'link' ? 'purple.500' : 'whiteAlpha.200'}
            _hover={{ bg: copyMode === 'link' ? 'purple.400' : 'whiteAlpha.300' }}
            transition="all 0.15s"
            onClick={() => setCopyMode('link')}
          >
            {'<link>'}
          </Box>
        </WrapItem>
        <WrapItem>
          <Box
            px={3}
            py={1}
            borderRadius="full"
            fontSize="small"
            cursor="pointer"
            bg={copyMode === 'import' ? 'purple.500' : 'whiteAlpha.200'}
            _hover={{ bg: copyMode === 'import' ? 'purple.400' : 'whiteAlpha.300' }}
            transition="all 0.15s"
            onClick={() => setCopyMode('import')}
          >
            @import
          </Box>
        </WrapItem>
      </Wrap>

      {/* Category filter */}
      <Wrap spacing={2} mb={4}>
        {FONT_CATEGORIES.map((cat) => (
          <WrapItem key={cat}>
            <Box
              px={3}
              py={1}
              borderRadius="full"
              fontSize="small"
              cursor="pointer"
              bg={selectedCategory === cat ? 'purple.500' : 'whiteAlpha.200'}
              _hover={{ bg: selectedCategory === cat ? 'purple.400' : 'whiteAlpha.300' }}
              transition="all 0.15s"
              onClick={() => setSelectedCategory(cat)}
              textTransform="capitalize"
            >
              {cat}
            </Box>
          </WrapItem>
        ))}
      </Wrap>

      {/* Search */}
      <Input
        placeholder="Search fonts..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        mb={4}
        size="sm"
        bg="whiteAlpha.100"
        border="none"
        _focus={{ bg: 'whiteAlpha.200', boxShadow: 'none' }}
      />

      {/* Font list */}
      <SimpleGrid columns={1} spacing={2}>
        {filteredFonts.map((font) => (
          <Box
            key={font.name}
            borderRadius="md"
            bg="whiteAlpha.100"
            _hover={{ bg: 'whiteAlpha.150' }}
            transition="all 0.15s"
            overflow="hidden"
          >
            {/* Font header */}
            <HStack
              justifyContent="space-between"
              alignItems="center"
              p={3}
              cursor="pointer"
              onClick={() => handleCopyFontFamily(font.name, font.category)}
            >
              <Box flex={1}>
                <Text
                  fontSize="large"
                  fontWeight="semibold"
                  style={{ fontFamily: `'${font.name}', ${font.category}` }}
                >
                  {font.name}
                </Text>
                <Text
                  fontSize="small"
                  opacity="secondary"
                  style={{ fontFamily: `'${font.name}', ${font.category}` }}
                  mt={1}
                >
                  The <em>quick</em> brown fox jumps over the lazy dog
                </Text>
              </Box>
              <IconButton
                aria-label="Toggle details"
                icon={
                  <ChevronDownIcon
                    transform={expandedFont === font.name ? 'rotate(180deg)' : 'rotate(0deg)'}
                    transition="transform 0.2s"
                  />
                }
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedFont(expandedFont === font.name ? null : font.name);
                }}
              />
            </HStack>

            {/* Expanded details */}
            <Collapse in={expandedFont === font.name}>
              <Box px={3} pb={3} pt={0}>
                <HStack spacing={2} mb={2}>
                  <Text fontSize="micro" opacity="secondary">
                    Weights: {font.weights.replace(/;/g, ', ')}
                  </Text>
                  <Text fontSize="micro" opacity="secondary">
                    | {font.category}
                  </Text>
                </HStack>
                <Box
                  p={2}
                  bg="blackAlpha.300"
                  borderRadius="sm"
                  fontFamily="mono"
                  fontSize="micro"
                  cursor="pointer"
                  _hover={{ bg: 'blackAlpha.400' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyFont(font);
                  }}
                >
                  {copyMode === 'link' ? generateLinkTag(font.name, font.weights) : generateCSSImport(font.name, font.weights)}
                </Box>
              </Box>
            </Collapse>
          </Box>
        ))}
      </SimpleGrid>

      {filteredFonts.length === 0 && (
        <Text fontSize="small" opacity="secondary" textAlign="center" py={8}>
          No fonts found. Try a different search term.
        </Text>
      )}

      {/* More options */}
      <Box mt={6} pt={4} pb={8} borderTopWidth="1px" borderColor="whiteAlpha.200">
        <Text fontSize="small" fontWeight="semibold" mb={2}>
          Browse More Fonts
        </Text>
        <Wrap spacing={3}>
          <WrapItem>
            <Link href="https://fonts.google.com" isExternal fontSize="small">
              Google Fonts
            </Link>
          </WrapItem>
          <WrapItem>
            <Link href="https://fonts.adobe.com" isExternal fontSize="small">
              Adobe Fonts
            </Link>
          </WrapItem>
          <WrapItem>
            <Link href="https://www.fontsquirrel.com" isExternal fontSize="small">
              Font Squirrel
            </Link>
          </WrapItem>
        </Wrap>
      </Box>
    </Box>
  );
};

export default FontsTab;
